/**
 * Editor Toolbar
 * 
 * Formatting toolbar for structured document editor.
 */

import { Editor } from '@tiptap/react';
import { useState, useRef, useEffect } from 'react';
import type { AeroDocument } from '../../types/document-schema';
import { BlockIdToggle } from '../BlockReference/BlockIdMargin';
import { documentVersionApi } from '../../services/api';
import { 
  useToast, 
  InputDialog, 
  Spinner,
} from '@/components/ui';
import { TableSizePicker } from './TableSizePicker';
import { DiagramPickerModal } from './DiagramPickerModal';
import { UndoActionsPanel } from './UndoActionsPanel';
import { logger } from '../../utils/logger';

export interface EditorToolbarProps {
  editor: Editor;
  document: AeroDocument;
  onSave: () => void;
  onToggleSplitView?: () => void;
  splitViewEnabled?: boolean;
  saving?: boolean;
  lastSaved?: Date | null;
  isOnline?: boolean;
  hideAutoSaveIndicator?: boolean;
  hideDocumentInfo?: boolean;
  onToggleVersionHistory?: () => void;
  versionHistoryEnabled?: boolean;
  projectId?: string;
  documentNumber?: string;
  isAIEditing?: boolean;
  showBlockIds?: boolean;
  onToggleBlockIds?: () => void;
  /** Callback to open generated PDF in the app's document viewer */
  onOpenPdfInViewer?: (pdfUrl: string, title: string) => void;
  /** Callback to open the search bar */
  onOpenSearch?: () => void;
}

export function EditorToolbar({ 
  editor, 
  document, 
  onSave: _onSave, 
  onToggleSplitView, 
  splitViewEnabled,
  saving = false,
  lastSaved,
  isOnline = true,
  hideAutoSaveIndicator = false,
  hideDocumentInfo = false,
  onToggleVersionHistory,
  versionHistoryEnabled,
  projectId,
  documentNumber,
  isAIEditing = false,
  showBlockIds = false,
  onToggleBlockIds,
  onOpenPdfInViewer: _onOpenPdfInViewer,
  onOpenSearch
}: EditorToolbarProps) {
  const [showUndoDropdown, setShowUndoDropdown] = useState(false);
  const [undoLoading, setUndoLoading] = useState(false);
  const [showImageDialog, setShowImageDialog] = useState(false);
  const [showTablePicker, setShowTablePicker] = useState(false);
  const [showDiagramPicker, setShowDiagramPicker] = useState(false);
  const [showUndoActionsPanel, setShowUndoActionsPanel] = useState(false);
  const undoDropdownRef = useRef<HTMLDivElement>(null);
  const tablePickerRef = useRef<HTMLDivElement>(null);
  const undoActionsPanelRef = useRef<HTMLDivElement>(null);
  
  const { error, warning } = useToast();
  
  // Close dropdowns when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (undoDropdownRef.current && !undoDropdownRef.current.contains(event.target as Node)) {
        setShowUndoDropdown(false);
      }
      if (tablePickerRef.current && !tablePickerRef.current.contains(event.target as Node)) {
        setShowTablePicker(false);
      }
      if (undoActionsPanelRef.current && !undoActionsPanelRef.current.contains(event.target as Node)) {
        setShowUndoActionsPanel(false);
      }
    }
    // Use window.document to avoid conflict with 'document' prop (AeroDocument)
    window.document.addEventListener('mousedown', handleClickOutside);
    return () => window.document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  // Undo to a specific time ago
  const handleUndoToMinutesAgo = async (minutes: number) => {
    if (!projectId || !documentNumber) return;
    
    try {
      setUndoLoading(true);
      logger.debug(`[EditorToolbar] Undoing to ${minutes} minutes ago`);
      
      // List versions
      const { versions } = await documentVersionApi.listVersions(projectId, documentNumber, 50);
      
      // Find the version closest to X minutes ago
      const targetTime = Date.now() - (minutes * 60 * 1000);
      
      // Filter versions older than target time, sorted newest-first as defense against unsorted responses
      const olderVersions = versions
        .filter(v => new Date(v.lastModified).getTime() <= targetTime)
        .sort((a, b) => new Date(b.lastModified).getTime() - new Date(a.lastModified).getTime());
      
      if (olderVersions.length === 0) {
        warning('No Version Found', `No version found from ${minutes} minutes ago. Try a different time range.`);
        return;
      }
      
      // Pick the most recent of the older versions
      const targetVersion = olderVersions[0];
      
      // Restore it
      await documentVersionApi.restoreVersion(projectId, documentNumber, targetVersion.versionId);
      
      setShowUndoDropdown(false);
      
      // Reload the document
      window.location.reload();
    } catch (err) {
      logger.error('[EditorToolbar] Failed to undo to previous version:', err);
      error('Restore Failed', 'Failed to restore previous version. Please try again.');
    } finally {
      setUndoLoading(false);
    }
  };
  
  const handleInsertImage = (url: string) => {
    if (url) {
      editor.chain().focus().setImage({ src: url }).run();
    }
  };
  
  if (!editor) {
    return null;
  }
  
  return (
    <>
      <div className="border-b border-border bg-muted px-3 py-2 flex items-center gap-2 flex-wrap">
        {/* Auto-save status indicator */}
        {!hideAutoSaveIndicator && (
          <>
            <div className="text-xs min-w-[130px]">
              {isAIEditing && (
                <span className="text-info animate-pulse font-medium flex items-center gap-1">
                  <Spinner size="sm" className="inline-block" />
                  AI editing...
                </span>
              )}
              {!isAIEditing && saving && <span className="text-warning animate-pulse font-medium">Saving...</span>}
              {!isAIEditing && !saving && lastSaved && isOnline && (
                <span className="text-muted-foreground">
                  Saved {lastSaved.toLocaleTimeString()}
                </span>
              )}
              {!isAIEditing && !saving && !lastSaved && isOnline && (
                <span className="text-success font-medium">Auto-save active</span>
              )}
              {!isOnline && (
                <span className="text-warning font-medium">Offline - saving locally</span>
              )}
            </div>
            
            {/* Split view toggle */}
            {onToggleSplitView && (
              <>
                <div className="w-px h-5 bg-secondary"></div>
                
                <button
                  onClick={onToggleSplitView}
                  className={`px-2 py-1 text-xs font-medium rounded transition-colors ${
                    splitViewEnabled 
                      ? 'bg-success text-success-foreground hover:bg-success/90' 
                      : 'bg-card text-foreground hover:bg-muted'
                  }`}
                  title={splitViewEnabled ? 'Close split view' : 'Open split view'}
                >
                  Split View
                </button>
              </>
            )}
            
            {/* Version history toggle */}
            {onToggleVersionHistory && (
              <>
                <div className="w-px h-5 bg-secondary"></div>
                
                <button
                  onClick={onToggleVersionHistory}
                  className={`px-2 py-1 text-xs font-medium rounded transition-colors ${
                    versionHistoryEnabled 
                      ? 'bg-primary text-primary-foreground hover:bg-primary/90' 
                      : 'bg-card text-foreground hover:bg-muted'
                  }`}
                  title={versionHistoryEnabled ? 'Close version history' : 'View version history'}
                >
                  <svg className="inline-block w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  History
                </button>
              </>
            )}
            
            {/* Find in document */}
            {onOpenSearch && (
              <>
                <div className="w-px h-5 bg-secondary"></div>
                
                <button
                  onClick={onOpenSearch}
                  className="px-2 py-1 text-xs font-medium rounded transition-colors bg-card text-foreground hover:bg-muted"
                  title="Find in document (Ctrl+F)"
                >
                  <svg className="inline-block w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  Find
                </button>
              </>
            )}
            
            {/* Quick Undo dropdown */}
            {projectId && documentNumber && (
              <>
                <div className="w-px h-5 bg-secondary"></div>
                
                <div className="relative" ref={undoDropdownRef}>
                  <button
                    onClick={() => setShowUndoDropdown(!showUndoDropdown)}
                    disabled={undoLoading}
                    className="px-2 py-1 text-xs font-medium rounded transition-colors bg-card text-foreground hover:bg-muted flex items-center gap-1 disabled:opacity-50"
                    title="Undo to previous version"
                  >
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                    </svg>
                    {undoLoading ? 'Restoring...' : 'Undo'}
                    <svg className="w-2 h-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </button>
                  
                  {showUndoDropdown && (
                    <div className="absolute top-full left-0 mt-1 bg-card border border-border rounded-lg shadow-lg z-50 min-w-[180px]">
                      <div className="p-1">
                        <button
                          onClick={() => handleUndoToMinutesAgo(5)}
                          className="w-full text-left px-3 py-2 text-sm text-foreground hover:bg-muted rounded"
                        >
                          Undo to 5 minutes ago
                        </button>
                        <button
                          onClick={() => handleUndoToMinutesAgo(15)}
                          className="w-full text-left px-3 py-2 text-sm text-foreground hover:bg-muted rounded"
                        >
                          Undo to 15 minutes ago
                        </button>
                        <button
                          onClick={() => handleUndoToMinutesAgo(30)}
                          className="w-full text-left px-3 py-2 text-sm text-foreground hover:bg-muted rounded"
                        >
                          Undo to 30 minutes ago
                        </button>
                        <button
                          onClick={() => handleUndoToMinutesAgo(60)}
                          className="w-full text-left px-3 py-2 text-sm text-foreground hover:bg-muted rounded"
                        >
                          Undo to 1 hour ago
                        </button>
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Recent Edits panel */}
                <div className="relative" ref={undoActionsPanelRef}>
                  <button
                    onClick={() => setShowUndoActionsPanel(!showUndoActionsPanel)}
                    className={`px-2 py-1 text-xs font-medium rounded transition-colors flex items-center gap-1 ${
                      showUndoActionsPanel
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-card text-foreground hover:bg-muted'
                    }`}
                    title="Recent Edits"
                  >
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                    </svg>
                    Edits
                  </button>
                  
                  <UndoActionsPanel
                    projectId={projectId}
                    documentNumber={documentNumber}
                    isOpen={showUndoActionsPanel}
                    onClose={() => setShowUndoActionsPanel(false)}
                    onUndoComplete={() => {
                      setShowUndoActionsPanel(false);
                      window.location.reload();
                    }}
                  />
                </div>
              </>
            )}
            
            <div className="w-px h-5 bg-secondary"></div>
          </>
        )}
        
        {/* Text formatting */}
        <button
          onClick={() => editor.chain().focus().toggleBold().run()}
          className={`px-2.5 py-1 text-xs font-bold rounded transition-colors ${
            editor.isActive('bold') 
              ? 'bg-primary text-primary-foreground' 
              : 'bg-card text-foreground hover:bg-muted'
          }`}
          title="Bold"
        >
          B
        </button>
        
        <button
          onClick={() => editor.chain().focus().toggleItalic().run()}
          className={`px-2.5 py-1 text-xs italic rounded transition-colors ${
            editor.isActive('italic') 
              ? 'bg-primary text-primary-foreground' 
              : 'bg-card text-foreground hover:bg-muted'
          }`}
          title="Italic"
        >
          I
        </button>
        
        <button
          onClick={() => editor.chain().focus().toggleCode().run()}
          className={`px-2.5 py-1 text-xs rounded transition-colors ${
            editor.isActive('code') 
              ? 'bg-primary text-primary-foreground' 
              : 'bg-card text-foreground hover:bg-muted'
          }`}
          title="Code"
        >
          {'</>'}
        </button>
        
        <div className="w-px h-5 bg-secondary"></div>
        
        {/* Headings */}
        <button
          onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
          className={`px-2 py-1 text-xs font-semibold rounded transition-colors ${
            editor.isActive('heading', { level: 1 }) 
              ? 'bg-primary text-primary-foreground' 
              : 'bg-card text-foreground hover:bg-muted'
          }`}
          title="Heading 1"
        >
          H1
        </button>
        
        <button
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          className={`px-2 py-1 text-xs font-semibold rounded transition-colors ${
            editor.isActive('heading', { level: 2 }) 
              ? 'bg-primary text-primary-foreground' 
              : 'bg-card text-foreground hover:bg-muted'
          }`}
          title="Heading 2"
        >
          H2
        </button>
        
        <button
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          className={`px-2 py-1 text-xs font-semibold rounded transition-colors ${
            editor.isActive('heading', { level: 3 }) 
              ? 'bg-primary text-primary-foreground' 
              : 'bg-card text-foreground hover:bg-muted'
          }`}
          title="Heading 3"
        >
          H3
        </button>
        
        <button
          onClick={() => editor.chain().focus().toggleHeading({ level: 4 }).run()}
          className={`px-2 py-1 text-xs font-semibold rounded transition-colors ${
            editor.isActive('heading', { level: 4 }) 
              ? 'bg-primary text-primary-foreground' 
              : 'bg-card text-foreground hover:bg-muted'
          }`}
          title="Heading 4"
        >
          H4
        </button>
        
        <button
          onClick={() => editor.chain().focus().toggleHeading({ level: 5 }).run()}
          className={`px-2 py-1 text-xs font-semibold rounded transition-colors ${
            editor.isActive('heading', { level: 5 }) 
              ? 'bg-primary text-primary-foreground' 
              : 'bg-card text-foreground hover:bg-muted'
          }`}
          title="Heading 5"
        >
          H5
        </button>
        
        <button
          onClick={() => editor.chain().focus().toggleHeading({ level: 6 }).run()}
          className={`px-2 py-1 text-xs font-semibold rounded transition-colors ${
            editor.isActive('heading', { level: 6 }) 
              ? 'bg-primary text-primary-foreground' 
              : 'bg-card text-foreground hover:bg-muted'
          }`}
          title="Heading 6"
        >
          H6
        </button>
        
        <div className="w-px h-5 bg-secondary"></div>
        
        {/* Lists */}
        <button
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          className={`px-2.5 py-1 text-xs rounded transition-colors ${
            editor.isActive('bulletList') 
              ? 'bg-primary text-primary-foreground' 
              : 'bg-card text-foreground hover:bg-muted'
          }`}
          title="Bullet List"
        >
          • List
        </button>
        
        <button
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          className={`px-2.5 py-1 text-xs rounded transition-colors ${
            editor.isActive('orderedList') 
              ? 'bg-primary text-primary-foreground' 
              : 'bg-card text-foreground hover:bg-muted'
          }`}
          title="Numbered List"
        >
          1. List
        </button>
        
        <div className="w-px h-5 bg-secondary"></div>
        
        {/* Table */}
        <div className="relative" ref={tablePickerRef}>
          <button
            onClick={() => setShowTablePicker(!showTablePicker)}
            className="px-2.5 py-1 text-xs rounded transition-colors bg-card text-foreground hover:bg-muted"
            title="Insert Table"
          >
            📊 Table
          </button>
          {showTablePicker && (
            <TableSizePicker
              onSelect={(rows, cols) => {
                editor.chain().focus().insertTable({ rows, cols, withHeaderRow: true }).run();
              }}
              onClose={() => setShowTablePicker(false)}
            />
          )}
        </div>
        
        {/* Image */}
        <button
          onClick={() => setShowImageDialog(true)}
          className="px-2.5 py-1 text-xs rounded transition-colors bg-card text-foreground hover:bg-muted"
          title="Insert Image"
        >
          🖼️ Image
        </button>
        
        {/* Diagram */}
        <button
          onClick={() => setShowDiagramPicker(true)}
          className="px-2.5 py-1 text-xs rounded transition-colors bg-card text-foreground hover:bg-muted"
          title="Insert Diagram (Mermaid)"
        >
          📊 Diagram
        </button>
        
        <div className="w-px h-5 bg-secondary"></div>
        
        {/* Block formatting */}
        <button
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          className={`px-2.5 py-1 text-xs rounded transition-colors ${
            editor.isActive('blockquote') 
              ? 'bg-primary text-primary-foreground' 
              : 'bg-card text-foreground hover:bg-muted'
          }`}
          title="Blockquote"
        >
          ❝ Quote
        </button>
        
        <button
          onClick={() => editor.chain().focus().toggleCodeBlock().run()}
          className={`px-2.5 py-1 text-xs rounded transition-colors ${
            editor.isActive('codeBlock') 
              ? 'bg-primary text-primary-foreground' 
              : 'bg-card text-foreground hover:bg-muted'
          }`}
          title="Code Block"
        >
          {'{ }'} Code
        </button>
        
        {/* Horizontal rule */}
        <button
          onClick={() => editor.chain().focus().setHorizontalRule().run()}
          className="px-2.5 py-1 text-xs rounded transition-colors bg-card text-foreground hover:bg-muted"
          title="Horizontal Rule"
        >
          ―
        </button>
        
        <div className="flex-1"></div>
        
        {/* Block IDs Toggle - for referencing blocks in chat with @ commands */}
        {onToggleBlockIds && (
          <BlockIdToggle 
            showBlockIds={showBlockIds} 
            onToggle={onToggleBlockIds} 
          />
        )}
        
        {/* Document info */}
        {!hideDocumentInfo && (
          <div className="text-xs text-muted-foreground font-medium">
            {document.metadata.documentNumber} Rev {document.metadata.currentRevision}
          </div>
        )}
      </div>
      
      {/* Image URL Dialog */}
      <InputDialog
        open={showImageDialog}
        onOpenChange={setShowImageDialog}
        title="Insert Image"
        description="Enter the URL of the image to insert"
        label="Image URL"
        placeholder="https://example.com/image.png"
        confirmText="Insert"
        onConfirm={handleInsertImage}
      />
      
      {/* Diagram Picker Modal */}
      {showDiagramPicker && projectId && (
        <DiagramPickerModal
          projectId={projectId}
          onClose={() => setShowDiagramPicker(false)}
          onInsert={(diagram) => {
            // Use insertVectorGraphic command from VectorGraphicNode extension
            editor.chain().focus().insertContent({
              type: 'vectorGraphic',
              attrs: {
                format: 'mermaid',
                content: diagram.content,
                caption: diagram.caption || null,
                diagramId: diagram.diagramId || null,
                diagramVersion: diagram.diagramVersion || null,
                diagramTitle: diagram.diagramTitle || null,
              },
            }).run();
            setShowDiagramPicker(false);
          }}
        />
      )}
    </>
  );
}

interface TipTapNode {
  type: string;
  attrs?: Record<string, unknown>;
  content?: TipTapNode[];
  text?: string;
}

/**
 * Convert document to TipTap format (placeholder implementation)
 */
function _convertDocumentToTipTap(document: AeroDocument): { type: 'doc'; content: TipTapNode[] } {
  // This is a simplified version - full implementation in StructuredEditor
  const content: TipTapNode[] = [];
  
  for (const section of document.body) {
    const headingLevel = section.type === 'section' ? 1 : section.type === 'subsection' ? 2 : 3;
    
    content.push({
      type: 'heading',
      attrs: { level: headingLevel },
      content: [
        {
          type: 'text',
          text: `${section.number} ${section.title}`
        }
      ]
    });
    
    for (const block of section.blocks) {
      if (block.type === 'paragraph') {
        content.push({
          type: 'paragraph',
          content: [
            {
              type: 'text',
              text: (block.content as { text?: string }).text || ''
            }
          ]
        });
      }
    }
  }
  
  return {
    type: 'doc',
    content
  };
}
